// Groups failed API calls in Sentry by the call that failed (KL-3).
//
// The stack of an AxiosError is axios internals, identical for every
// endpoint, so Sentry put every failed request in the app into one issue.
// The fingerprint below splits it by method, status and endpoint so each
// failure can be triaged on its own. It only groups; nothing is dropped.

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const LONG_HEX = /^[0-9a-f]{16,}$/i;

function normalizeSegment(segment) {
  if (/^\d+$/.test(segment) || UUID.test(segment) || LONG_HEX.test(segment)) {
    return ':id';
  }
  // Percent-encoded segments are user text (a search term with a space or
  // non-ASCII letters); keeping them would make one group per term.
  if (segment.includes('%')) {
    return ':term';
  }
  return segment;
}

export function normalizeEndpoint(url, baseURL) {
  if (!url && !baseURL) return 'unknown';

  let parsed;
  try {
    // Mirror axios: a relative url is appended to baseURL's path.
    const isAbsolute = /^[a-z][a-z\d+\-.]*:\/\//i.test(url || '');
    const full = isAbsolute
      ? url
      : `${(baseURL || '').replace(/\/+$/, '')}/${(url || '').replace(
          /^\/+/,
          ''
        )}`;
    parsed = new URL(full, 'https://unknown');
  } catch (e) {
    return 'unknown';
  }

  const path = parsed.pathname
    .split('/')
    .filter(Boolean)
    .map(normalizeSegment)
    .join('/');

  return path ? `${parsed.host}/${path}` : parsed.host;
}

export function describeAxiosError(error) {
  if (!error || typeof error !== 'object') return null;
  if (!error.isAxiosError && error.name !== 'AxiosError') return null;

  const config = error.config || {};
  return {
    method: (config.method || 'get').toUpperCase(),
    status: error.response ? String(error.response.status) : 'no-response',
    endpoint: normalizeEndpoint(config.url, config.baseURL)
  };
}

export function groupAxiosError(event, error) {
  const description = describeAxiosError(error);
  if (!description) return event;

  const { method, status, endpoint } = description;
  event.fingerprint = ['axios-error', method, status, endpoint];
  event.tags = {
    ...(event.tags || {}),
    'api.method': method,
    'api.status': status,
    'api.endpoint': endpoint
  };
  return event;
}
